import React, { useState, useEffect } from "react";
import { useMutation } from "@apollo/client";
import gql from "graphql-tag";
import { X } from "lucide-react";
import "./SupportView.css";
import { useTranslation } from "react-i18next";  
const CREATE_TICKET = gql`
  mutation CreateTicket(
    $username: String!
    $subject: String!
    $message: String!
    $attachment: AttachmentInput
  ) {
    createTicket(
      username: $username
      subject: $subject
      message: $message
      attachment: $attachment
    ) {
      success
      message
    }
  }
`;

const SupportView = () => {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    subject: "",
    discordUsername: "",
    message: ""
  });

  const [file, setFile] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const [createTicket, { loading: isSubmitting }] = useMutation(CREATE_TICKET);

  // Hook de traducción
  const { t } = useTranslation();

  useEffect(() => {
    const isValid = Object.values(form).every((value) => value.trim() !== "");
    setIsFormValid(isValid);
  }, [form]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];

    if (uploadedFile && uploadedFile.size > 8 * 1024 * 1024) {
      alert(t("supportView.fileAlerts.fileSizeExceeded"));
      e.target.value = null;
      return;
    }

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain"
    ];

    if (uploadedFile && !allowedTypes.includes(uploadedFile.type)) {
      alert(t("supportView.fileAlerts.fileTypeNotSupported"));
      e.target.value = null;
      return;
    }

    setFile(uploadedFile);
  };

  const removeFile = () => setFile(null);

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result.split(",")[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);

    try {
      const variables = {
        username: form.discordUsername,
        subject: form.subject,
        message: form.message
      };

      if (file) {
        const base64Content = await convertFileToBase64(file);
        variables.attachment = {
          filename: file.name,
          content: base64Content,
          contentType: file.type
        };
      }

      const { data } = await createTicket({
        variables: variables
      });

      if (data?.createTicket?.success) {
        setSubmitStatus("success");
        setForm({
          fullName: "",
          email: "",
          subject: "",
          discordUsername: "",
          message: ""
        });
        setFile(null);
        setTimeout(() => {
          setSubmitStatus(null);
        }, 4000);
      } else {
        throw new Error(data?.createTicket?.message || "Error creating ticket");
      }
    } catch (error) {
      console.error("Error:", error);
      setSubmitStatus("error");
    }
  };

  return (
    <div>
      <div className="support-header">
        <h1>{t("supportView.header.title")}</h1>
        <p>{t("supportView.header.subtitle")}</p>
      </div>

      <form className="support-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label>{t("supportView.form.fullNameLabel")}</label>
          <input name="fullName" value={form.fullName} onChange={handleChange} />
        </div>

        <div className="form-group">
          <label>{t("supportView.form.discordUsernameLabel")}</label>
          <input
            name="discordUsername"
            value={form.discordUsername}
            onChange={handleChange}
            required
            placeholder={t("supportView.form.discordUsernamePlaceholder")}
          />
          <small className="help-text">
            {t("supportView.form.discordHelpText")}
          </small>
        </div>

        <div className="form-group">
          <label>{t("supportView.form.emailLabel")}</label>
          <input
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>{t("supportView.form.subjectLabel")}</label>
          <input name="subject" value={form.subject} onChange={handleChange} />
        </div>

        <div className="form-group">
          <label>{t("supportView.form.messageLabel")}</label>
          <textarea
            name="message"
            value={form.message}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>{t("supportView.form.attachmentLabel")}</label>
          <div className="file-upload-container">
            <input
              type="file"
              id="file-upload"
              onChange={handleFileUpload}
              style={{ display: "none" }}
            />
            <label htmlFor="file-upload" className="file-upload-button">
              {t("supportView.form.fileUploadBtn")}
            </label>

            {file && (
              <div className="selected-file">
                <span>{file.name}</span>
                <button type="button" onClick={removeFile}>
                  <X size={16} />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="submit-container">
          <button
            type="submit"
            className="submit-button"
            disabled={!isFormValid || isSubmitting}
          >
            {isSubmitting
              ? t("supportView.form.submitting")
              : t("supportView.form.submitBtn")}
          </button>
        </div>

        {submitStatus === "success" && (
          <div className="success-message">
            {t("supportView.form.successMessage")}
          </div>
        )}

        {submitStatus === "error" && (
          <div className="error-message">
            {t("supportView.form.errorMessage")}
          </div>
        )}
      </form>
    </div>
  );
};

export default SupportView;
